import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { useAuthStore } from '../../store/authStore';
import BrandHeader from '../../components/brand/BrandHeader';

type BrandProfile = {
  id: string;
  user_id: string;
  company_name: string;
  description?: string;
  website?: string;
  industry?: string;
  logo_url?: string;
  contact_email?: string;
  contact_phone?: string;
};

export default function BrandProfile() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [profile, setProfile] = useState<BrandProfile | null>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [is_brand, set_is_brand] = useState(false);
  const [flag_loading, set_flag_loading] = useState(false);
  const [account, set_account] = useState<any>(null);
  const [account_loading, set_account_loading] = useState(false);
  const [logo_uploading, set_logo_uploading] = useState(false);
  const [show_deposit_modal, set_show_deposit_modal] = useState(false);
  const [deposit_amount, set_deposit_amount] = useState('');
  const [deposit_loading, set_deposit_loading] = useState(false);
  const [deposit_err, set_deposit_err] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const profileResult = await back_app.bprofile_get();
      if (!profileResult.err && profileResult) {
        setProfile(profileResult);
      }

      const campaignsResult = await back_app.campaign_list();
      if (!campaignsResult.err && Array.isArray(campaignsResult)) {
        setCampaigns(campaignsResult);
      }

      const assetsResult = await back_app.basset_list();
      if (!assetsResult.err && Array.isArray(assetsResult)) {
        setAssets(assetsResult);
      }

      if (user?.email) {
        const org_res = await back_app.org_get();
        if (org_res?.flag?.is_brand)
          set_is_brand(true);
      }

      const acc_res = await back_app.bprofile_account_get();
      if (!acc_res?.err)
        set_account(acc_res?.account || null);
    } catch (error) {
      console.error('Error fetching profile:', error);
      if ((error as Error)?.message == 'no session') navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const create_account = async () => {
    set_account_loading(true);
    try {
      const res = await back_app.bprofile_account_insert();
      if (!res?.err)
        set_account(res?.account || null);
    } catch (e) {
      console.error('create account failed', e);
    }
    set_account_loading(false);
  };

  const open_deposit = () => {
    set_deposit_amount('');
    set_deposit_err('');
    set_show_deposit_modal(true);
  };

  const submit_deposit = async () => {
    set_deposit_err('');
    const amount = parseFloat(deposit_amount);
    if (!amount || !isFinite(amount) || amount <= 0)
    {
      set_deposit_err('Enter a positive amount');
      return;
    }
    set_deposit_loading(true);
    try {
      const res = await back_app.bprofile_account_deposit(amount);
      if (res?.err)
      {
        set_deposit_err(res.err);
        set_deposit_loading(false);
        return;
      }
      set_account(res?.account || null);
      set_show_deposit_modal(false);
    } catch (e: any) {
      set_deposit_err(e?.message || 'Deposit failed');
    }
    set_deposit_loading(false);
  };

  const handle_logo_upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/'))
    {
      alert('Please select an image file (PNG, JPG)');
      return;
    }
    if (file.size > 2 * 1024 * 1024)
    {
      alert('Logo must be less than 2MB');
      return;
    }
    set_logo_uploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('name', 'logo');
      form.append('asset_type', 'logo');
      const up = await back_app.basset_insert(form);
      if (up?.err)
        throw new Error(up.err);
      const url = up.file_url || up.url;
      if (!url)
        throw new Error('No URL returned');
      const save = await back_app.bprofile_set({logo_url: url});
      if (save?.err)
        throw new Error(save.err);
      setProfile(prev => prev ? {...prev, logo_url: url} : prev);
    } catch (err: any) {
      console.error('logo upload failed', err);
      alert('Logo upload failed: ' + (err?.message || 'unknown error'));
    }
    set_logo_uploading(false);
  };

  const toggle_brand_flag = async () => {
    set_flag_loading(true);
    try {
      let next = !is_brand;
      let res = await back_app.org_set('flag.is_brand', next);
      console.log('toggle flag.is_brand', next, res);
      if (!res?.err)
        set_is_brand(next);
    } catch (e) {
      console.error('toggle brand flag failed', e);
    }
    set_flag_loading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">👤</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile not found</h2>
          <p className="text-gray-600 mb-6">Please complete your profile setup</p>
          <button
            onClick={() => navigate('/brand/profile/edit')}
            className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
          >
            Complete Profile
          </button>
        </div>
      </div>
    );
  }

  const activeCampaigns = campaigns.filter(c => c.status == 'active').length;
  const totalBudget = campaigns.reduce((sum, c) => sum + (c.budget_info?.total_budget || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      {}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button
            onClick={() => navigate('/brand/dashboard')}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-4 transition-colors"
          >
            <span>←</span>
            <span>Back to Dashboard</span>
          </button>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {}
            <div className="relative flex-shrink-0">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/20 backdrop-blur border-4 border-white/30 flex items-center justify-center overflow-hidden">
                {profile.logo_url ? (
                  <img src={profile.logo_url} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center">
                    <span className="text-4xl sm:text-5xl text-white font-bold">
                      {profile.company_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image}
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">{profile.company_name}</h2>
              {profile.industry && (
                <p className="text-white/90 mb-3">{profile.industry}</p>
              )}
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-white/20 backdrop-blur rounded-full text-sm font-semibold">
                  {campaigns.length} Campaigns
                </span>
                <span className="px-3 py-1 bg-white/20 backdrop-blur rounded-full text-sm font-semibold">
                  {assets.length} Assets
                </span>
              </div>
            </div>

            {}
            <button
              onClick={() => navigate('/brand/profile/edit')}
              className="hidden sm:block px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
            >
              Edit Profile
            </button>
          </div>

          {}
          <button
            onClick={() => navigate('/brand/profile/edit')}
            className="sm:hidden w-full mt-4 px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:shadow-lg transition-all"
          >
            Edit Profile
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-sm text-gray-600 mb-1">Total Campaigns</div>
            <div className="text-2xl font-bold text-gray-900">{campaigns.length}</div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-sm text-gray-600 mb-1">Active</div>
            <div className="text-2xl font-bold text-green-600">{activeCampaigns}</div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-sm text-gray-600 mb-1">Total Budget</div>
            <div className="text-2xl font-bold text-blue-600">${totalBudget.toLocaleString()}</div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="text-sm text-gray-600 mb-1">Assets</div>
            <div className="text-2xl font-bold text-purple-600">{assets.length}</div>
          </div>
        </div>

        {}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Account</h3>
            {account && (
              <button
                onClick={open_deposit}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold text-sm hover:shadow-lg"
              >
                + Add Money
              </button>
            )}
          </div>
          {account ? (
            <div>
              <div className="text-sm text-gray-600 mb-1">Balance</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                ${(account.balance || 0).toLocaleString(undefined,
                  {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
              {!!account.pending_out && (
                <div className="text-xs text-gray-500">
                  ${account.pending_out.toLocaleString()} held in pending transfers
                </div>
              )}
              <div className="text-xs text-gray-400 mt-3 break-all">
                Account ID: {account.id}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-600 mb-4">
                You don't have an account yet. Create one to fund campaigns.
              </p>
              <button
                onClick={create_account}
                disabled={account_loading}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
              >
                {account_loading ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          )}
        </div>

        {}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Company Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Company Name
              </label>
              <p className="text-gray-900 font-medium">{profile.company_name}</p>
            </div>

            {profile.industry && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Industry
                </label>
                <p className="text-gray-900 font-medium">{profile.industry}</p>
              </div>
            )}

            {profile.website && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Website
                </label>
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline font-medium break-all"
                >
                  {profile.website}
                </a>
              </div>
            )}

            {profile.contact_email && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Email
                </label>
                <a
                  href={`mailto:${profile.contact_email}`}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {profile.contact_email}
                </a>
              </div>
            )}

            {profile.contact_phone && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Phone
                </label>
                <a
                  href={`tel:${profile.contact_phone}`}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {profile.contact_phone}
                </a>
              </div>
            )}

          </div>

          {profile.description && (
            <div className="mt-6 pt-6 border-t">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Company Description
              </label>
              <p className="text-gray-900 leading-relaxed">{profile.description}</p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  Brand Account
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Enable to access campaign creation and management
                </p>
              </div>
              <button
                onClick={toggle_brand_flag}
                disabled={flag_loading}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                  is_brand ? 'bg-green-500' : 'bg-gray-300'
                } ${flag_loading ? 'opacity-50' : ''}`}
              >
                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                  is_brand ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {is_brand && (
              <p className="text-xs text-green-600 mt-2 font-medium">Brand flag is active</p>
            )}
            {!is_brand && (
              <p className="text-xs text-red-500 mt-2 font-medium">
                Brand flag is not set - campaigns will not load
              </p>
            )}
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div
            onClick={() => navigate('/brand/campaigns')}
            className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">View Campaigns</h3>
                <p className="text-sm text-gray-600">Manage your {campaigns.length} campaigns</p>
              </div>
            </div>
          </div>

          <div
            onClick={() => navigate('/brand/assets')}
            className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">View Assets</h3>
                <p className="text-sm text-gray-600">Browse your {assets.length} assets</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {show_deposit_modal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Add Money
                </h3>
                <button
                  onClick={() => set_show_deposit_modal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                How much would you like to add to your account?
              </p>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Amount (USD)
              </label>
              <div className="relative mb-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={deposit_amount}
                  onChange={(e) => set_deposit_amount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  className="w-full pl-7 pr-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {deposit_err && (
                <p className="text-sm text-red-600 mb-2">{deposit_err}</p>
              )}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => set_show_deposit_modal(false)}
                  disabled={deposit_loading}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-semibold disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submit_deposit}
                  disabled={deposit_loading || !deposit_amount}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                >
                  {deposit_loading ? 'Adding...' : 'Add Money'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}